import { useState, useRef } from 'react';
import { useMutation } from 'react-query';
import {
  Box,
  Typography,
  TextField,
  Button,
  Avatar,
  Divider,
  Alert,
  CircularProgress,
  Paper,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import CameraAltRoundedIcon from '@mui/icons-material/CameraAltRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import { useAuth } from '../context/AuthContext';
import { authAPI, adminAPI } from '../services/api';
import AdminBackLink from '../components/AdminBackLink';
import { PageHeader } from '../components/ui';

function roleBadgeColor(role) {
  const r = String(role || '').toLowerCase().replace(/[\s-]+/g, '_');
  if (r === 'super_admin') return 'warning';
  if (r === 'admin') return 'primary';
  return 'default';
}

function formatRoleLabel(role) {
  if (!role) return 'Admin';
  const r = String(role).trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (r === 'super_admin') return 'Super Admin';
  return String(role).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function AdminProfile() {
  const { user, refreshUser } = useAuth();
  const fileInputRef = useRef(null);

  const [firstName, setFirstName] = useState(user?.firstName || '');
  const [lastName, setLastName] = useState(user?.lastName || '');
  const [avatarPreview, setAvatarPreview] = useState(user?.avatar || null);
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState(null);

  const initials = `${(firstName || user?.firstName || '')[0] || ''}${(lastName || user?.lastName || '')[0] || ''}`.toUpperCase() || 'A';

  const saveMutation = useMutation(
    (data) => authAPI.updateMe(data),
    {
      onSuccess: async () => {
        await refreshUser();
        setMsg({ type: 'success', text: 'Profile saved.' });
      },
      onError: (err) => {
        setMsg({ type: 'error', text: err.response?.data?.message || 'Failed to save profile.' });
      }
    }
  );

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setMsg(null);
    try {
      const res = await adminAPI.uploadImage(file);
      const url = res.data?.url || res.data?.data?.url || res.data?.imageUrl;
      if (!url) throw new Error('No URL returned from upload.');
      setAvatarPreview(url);
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.message || 'Image upload failed.' });
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleRemoveImage = () => {
    setAvatarPreview(null);
  };

  const handleSave = (e) => {
    e.preventDefault();
    setMsg(null);
    if (!firstName.trim() || !lastName.trim()) {
      setMsg({ type: 'error', text: 'First and last name are required.' });
      return;
    }
    saveMutation.mutate({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      avatar: avatarPreview || null
    });
  };

  return (
    <div className="module-page">
      <AdminBackLink />
      <PageHeader title="My Profile" subtitle="Update your name and profile photo." />

      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, maxWidth: 600 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <PersonRoundedIcon fontSize="small" color="action" />
          <Typography variant="subtitle1" fontWeight={600}>
            Edit profile
          </Typography>
        </Box>
        <Divider sx={{ mb: 3 }} />

        {/* Avatar upload */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, mb: 3 }}>
          <Box sx={{ position: 'relative', flexShrink: 0 }}>
            <Avatar
              src={avatarPreview || undefined}
              sx={{
                width: 80,
                height: 80,
                bgcolor: 'primary.main',
                fontSize: '1.75rem',
                fontWeight: 700
              }}
            >
              {!avatarPreview ? initials : null}
            </Avatar>
            {uploading && (
              <Box
                sx={{
                  position: 'absolute', inset: 0, borderRadius: '50%',
                  bgcolor: 'rgba(0,0,0,0.45)', display: 'flex',
                  alignItems: 'center', justifyContent: 'center'
                }}
              >
                <CircularProgress size={22} sx={{ color: '#fff' }} />
              </Box>
            )}
          </Box>

          <Box>
            <Box sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<CameraAltRoundedIcon fontSize="small" />}
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                {avatarPreview ? 'Change photo' : 'Upload photo'}
              </Button>
              {avatarPreview && (
                <Tooltip title="Remove photo">
                  <IconButton size="small" onClick={handleRemoveImage} disabled={uploading}>
                    <DeleteRoundedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            <Typography variant="caption" color="text.secondary">
              JPG, PNG or WebP · max 5 MB
            </Typography>
          </Box>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            style={{ display: 'none' }}
            onChange={handleImageChange}
          />
        </Box>

        {/* Role badge (read-only) */}
        <Box sx={{ mb: 3 }}>
          <Chip
            size="small"
            color={roleBadgeColor(user?.role)}
            label={formatRoleLabel(user?.role)}
          />
          {user?.phone ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
              Phone: {user.phone}
            </Typography>
          ) : null}
        </Box>

        {/* Name fields */}
        <Box component="form" onSubmit={handleSave} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="First name"
              size="small"
              fullWidth
              required
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
            <TextField
              label="Last name"
              size="small"
              fullWidth
              required
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          </Box>

          {msg ? (
            <Alert severity={msg.type} onClose={() => setMsg(null)} sx={{ py: 0.5 }}>
              {msg.text}
            </Alert>
          ) : null}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              type="submit"
              variant="contained"
              disabled={saveMutation.isLoading || uploading}
              startIcon={saveMutation.isLoading ? <CircularProgress size={16} /> : null}
            >
              {saveMutation.isLoading ? 'Saving…' : 'Save changes'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </div>
  );
}
